// MainLayout — the Hiring console frame: the design system's AppShell
// (Sidebar, top bar, Breadcrumb, TenantWatermark, assistant slot; phone,
// tablet and desktop), as in ui_kits/hiring.
//
// On the school's own Hiring app (hiring.<school>.equaos.in, standalone.js)
// the Sidebar carries only Hiring's own pages, under that school's chip, with
// the signed-in person and Sign out (to the school's sign-out). There is no
// school switcher: the school is the address's.
//
// Inside the Equall shell, the shell already shows the modules, the school,
// the signed-in person, switching and sign-out, so the Sidebar carries only
// Hiring's own pages and those controls are hidden (as since #2).
//
// Anywhere else (the legacy domain, a local `npm start`) the Sidebar is the
// platform module map (guidelines/moduleMap.js) with Hiring active, and
// Hiring's own pages are Tabs at the top of the content.
//
// On a desktop the Sidebar collapses to its icon rail and back, from its own
// last entry, and remembers which.
//
// Screens on the design system pass `ds` (see App.js) and render straight
// into AppShell's content grid. Screens not yet migrated render inside
// .dps-legacy, edge to edge, with the DPS styling they had.

import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { authService } from '../../services/auth.service';
import { APP_BASE, SCHOOL_ADDRESS, PLATFORM_TIER } from '../../config/base';
import { inShell, inFrame, shellPagesTaken } from '../../shell';
import {
  standalone, signOut as standaloneSignOut, schoolProfiles, switchToSchool, landingPath,
} from '../../standalone';
import { ADMIN_PAGES, HIRING_PAGES, adminPagesFor, navBlocks } from '../../dashboard/adminPages';
import {
  AppShell, Tabs, Button, Select, StatusPill, TenantWatermark, Icon,
  MODULE_GROUPS, STATUS_TONE, STATUS_LABEL, ShellLayoutContext,
} from '../../ds';
import NoticeBell from './NoticeBell';
import PersonMenu from './PersonMenu';
import CopilotPanel, { CopilotButton } from '../copilot/CopilotPanel';
import { useCopilot } from '../copilot/CopilotContext';
import SharedAssistant from '../assistant/SharedAssistant';
import useAssistantChoice from '../assistant/useAssistantChoice';

const HIRING_KEY = 'hir';

/* The assistant here is the shared EquaOS assistant (one assistant, Shohini
   9 Oct 2026), on Hiring's own MCP tools and agents, with read-back cards to
   approve. The older Copilot panel shows instead only while the server has
   COPILOT_PANEL=on (one release, to switch back). Inside the ERP shell the
   shell's own assistant is on screen, so neither is drawn here. */

/* One person, several schools: the switcher. Rendered only when the signed-in
   email has active profiles at more than one school. Switching asks the Auth
   Gateway to move the session (no password retyped), stores the fresh tokens
   for the target school, and reloads — every screen behind it is school-scoped
   and must be re-read as the other school. */
function useSchoolProfiles() {
  const [profiles, setProfiles] = useState([]);
  useEffect(() => {
    if (inShell) return;
    // The school's own Hiring app asks the gateway on its address; anywhere
    // else, Hiring's API does (the older console).
    const ask = standalone ? schoolProfiles() : authService.profiles();
    ask.then(setProfiles).catch(() => setProfiles([]));
  }, []);
  return profiles;
}

/* On the school's own Hiring app the school card in the sidebar is the
   switcher (T2 parity, Shohini 9 Oct 2026): picking another school lands on
   that school's Hiring app, signed in, on the same section. Only the schools
   the gateway lists for this person (a login there, or a group
   administrator's group). */
export function SchoolCardSwitcher({ profiles, here }) {
  const [switching, setSwitching] = useState(false);
  const [error, setError] = useState('');
  const current = profiles.find((p) => p.current);
  const onChange = async (tenant) => {
    if (!tenant || tenant === current?.tenant) return;
    setSwitching(true);
    setError('');
    try {
      const sections = [...HIRING_PAGES, ...ADMIN_PAGES].map((x) => x.value);
      await switchToSchool(tenant, landingPath(here, sections));
    } catch (err) {
      setSwitching(false);
      setError(err?.message || 'Could not switch school. Try again.');
    }
  };
  return (
    <div onClick={(e) => e.stopPropagation()} style={{ marginTop: 4 }}>
      <Select
        value={current?.tenant || ''}
        options={profiles.map((p) => ({ value: p.tenant, label: p.name }))}
        placeholder="Switch school"
        onChange={onChange}
        disabled={switching}
      />
      {switching && <div role="status" style={{ marginTop: 4 }}>Switching…</div>}
      {error && <div role="alert" style={{ marginTop: 4, color: 'var(--danger, #b42318)' }}>{error}</div>}
    </div>
  );
}

function SchoolSwitcher({ profiles }) {
  const [switching, setSwitching] = useState(false);
  if (profiles.length < 2) return null;
  const current = profiles.find((p) => p.current);

  const onChange = async (tenant) => {
    if (!tenant || tenant === current?.tenant) return;
    setSwitching(true);
    try {
      await authService.switchSchool(tenant);
      window.location.href = `${APP_BASE}/dashboard`;
    } catch {
      setSwitching(false);
      alert('Could not switch school — try signing in again.');
    }
  };

  return (
    <div style={{ minWidth: 200 }}>
      <Select
        value={current?.tenant || ''}
        options={profiles.map((p) => ({ value: p.tenant, label: p.name }))}
        placeholder="School"
        onChange={onChange}
        disabled={switching}
      />
    </div>
  );
}

/* Inside the ERP, once the ERP lists Hiring's pages in its own sidebar
   (shell.js offerPages): no rail or top bar of Hiring's own, because the ERP
   draws the navigation, the breadcrumb and the assistant. Hiring's actions
   sit above the screen. Screens still get the layout they read. */
const frameLayout = () => {
  const w = typeof window !== 'undefined' ? window.innerWidth : 1280;
  const bp = w < 768 ? 'mobile' : w < 1100 ? 'tablet' : 'desktop';
  return { bp, mobile: bp === 'mobile', tablet: bp === 'tablet', desktop: bp === 'desktop', pad: bp === 'mobile' ? 16 : bp === 'tablet' ? 24 : 32 };
};

function useFrameLayout() {
  const [layout, setLayout] = useState(frameLayout);
  useEffect(() => {
    const onResize = () => setLayout(frameLayout());
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return layout;
}

/* In the EquaOS frame (Hiring's own address, frame.js): the frame draws the
   sidebar, top bar, school, person menu, assistant and background, as for
   People. Hiring draws its screens only, into the frame's content area. */
function InFrame({ ds, children }) {
  const layout = useFrameLayout();
  return (
    <div className="eq-ds" style={{ minWidth: 0, fontFamily: 'var(--font-body)', color: 'var(--text-1)' }}>
      <ShellLayoutContext.Provider value={layout}>
        {ds ? children : <div className="dps-legacy" style={{ minWidth: 0 }}>{children}</div>}
      </ShellLayoutContext.Provider>
    </div>
  );
}

function FramedInErp({ ds, actions, children }) {
  const layout = useFrameLayout();
  const top = layout.mobile ? 16 : 28;
  const bottom = layout.mobile ? 24 : 72;
  return (
    <div className="eq-ds eq-root" style={{ minHeight: '100%', background: 'var(--page-bg)', fontFamily: 'var(--font-body)' }}>
      <div style={{ padding: `${top}px ${layout.pad}px ${bottom}px`, display: 'grid', gap: layout.mobile ? 16 : 20, maxWidth: ds ? 1240 : 'none' }}>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, flexWrap: 'wrap' }}>{actions}</div>
        <ShellLayoutContext.Provider value={layout}>
          {ds ? children : (
            <div className="dps-legacy" style={{ margin: `0 -${layout.pad}px -${bottom}px`, minWidth: 0 }}>
              {children}
            </div>
          )}
        </ShellLayoutContext.Provider>
      </div>
    </div>
  );
}

/* The Sidebar collapses to its icon rail (AppShell's tablet layout) and
   back. Desktop only: a tablet already has the rail, a phone the drawer. */
const COLLAPSE_KEY = 'hiring.sidebar.collapsed';
const COLLAPSE_ITEM = '__sidebar';
const isDesktop = () => typeof window !== 'undefined' && window.innerWidth >= 1100;

function useCollapsibleSidebar() {
  const [desktop, setDesktop] = useState(isDesktop);
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem(COLLAPSE_KEY) === '1'; } catch { return false; }
  });
  useEffect(() => {
    const onResize = () => setDesktop(isDesktop());
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  const toggle = () => setCollapsed((was) => {
    try { localStorage.setItem(COLLAPSE_KEY, was ? '0' : '1'); } catch { /* this visit only */ }
    return !was;
  });
  return { desktop, collapsed: desktop && collapsed, toggle };
}

/* The menu's blocks fold, as the ERP's do (frame 1.6.0, C16, Sanjoy 29 Oct
   2026), and the app remembers which are folded. The design system's Sidebar
   draws a block's heading as text only, so a block's first entry is its fold
   control: the caret and the block's name. Hiring's own block (the pages with
   no block) does not fold. */
const FOLD_KEY = 'hiring.sidebar.folded';
const FOLD_ITEM = 'fold:';

function useFoldedBlocks() {
  const [folded, setFolded] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem(FOLD_KEY) || '[]')); } catch { return new Set(); }
  });
  const toggle = (label) => setFolded((was) => {
    const next = new Set(was);
    if (next.has(label)) next.delete(label); else next.add(label);
    try { localStorage.setItem(FOLD_KEY, JSON.stringify([...next])); } catch { /* this visit only */ }
    return next;
  });
  return { folded, toggle };
}

/* The Sidebar's groups for Hiring's own pages, in blocks. A block opens by
   itself while you are on one of its pages; on the icon rail nothing folds. */
export function foldedGroups(blocks, { folded, activeKey, rail, icon }) {
  return blocks.map((b, i) => {
    const pages = b.pages.map((p) => ({ key: p.value, label: p.label, icon: icon(p.icon, 18) }));
    // A distinct heading per block (the Sidebar keys groups by heading): the
    // first block is "Hiring"; the folding ones carry their name on the fold row.
    const label = i === 0 ? b.label : '\u200b'.repeat(i);
    if (i === 0 || rail) return { label: i === 0 ? b.label : label, items: pages };
    const open = !folded.has(b.label) || b.pages.some((p) => p.value === activeKey);
    return {
      label,
      items: [{ key: FOLD_ITEM + b.label, label: b.label, icon: icon(open ? 'caret-down' : 'caret-right', 14) }, ...(open ? pages : [])],
    };
  });
}

const pageFor = (pathname, pages) =>
  pages.find((p) => pathname === p.value || pathname.startsWith(`${p.value}/`)) || null;

function crumbsFor(pathname, page) {
  const crumbs = ['Hiring'];
  if (page && ADMIN_PAGES.includes(page)) crumbs.push('Admin');
  if (page) crumbs.push(page.label);
  if (pathname === '/jobs/create') crumbs.push('New JD');
  else if (pathname === '/jobs/generate') crumbs.push('Generated JD');
  else if (/^\/jobs\/[^/]+\/reports/.test(pathname)) crumbs.push('Interview reports');
  else if (/^\/jobs\/[^/]+\/human-interview/.test(pathname)) crumbs.push('Interview panel');
  else if (/^\/jobs\/[^/]+/.test(pathname)) crumbs.push('Job');
  else if (pathname.startsWith('/applications/')) crumbs.push('Application');
  else if (pathname.startsWith('/admin/final-interview')) crumbs.push('Final interview');
  return crumbs;
}

// "DPS Pune East" → "DPS" (a leading acronym is the school's short name);
// "Priya Rao" → "PR".
function initialsOf(text) {
  const words = String(text || '').split(/\s+/).filter(Boolean);
  if (words.length && /^[A-Z]{2,4}$/.test(words[0])) return words[0];
  return words
    .slice(0, 2)
    .map((s) => s[0].toUpperCase())
    .join('');
}

const MainLayout = ({ children, ds = false }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const profiles = useSchoolProfiles();
  const sidebar = useCollapsibleSidebar();
  const blocksFold = useFoldedBlocks();
  // Hiring's own pages in the Sidebar: in the shell, and on the school's own app.
  const ownPages = inShell || standalone;

  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isAdmin = user.role === 'admin';
  const hiring = HIRING_PAGES;
  const admin = isAdmin ? adminPagesFor(PLATFORM_TIER) : [];
  const pages = [...hiring, ...admin];
  const { pathname } = location;
  const page = pageFor(pathname, pages);
  const isSectionRoot = Boolean(page && page.value === pathname);

  const copilotContext = useCopilot();
  const choice = useAssistantChoice();
  // The Copilot panel only when the server keeps it on (COPILOT_PANEL).
  const copilot = choice === 'copilot' ? copilotContext : null;
  const handleLogout = () => {
    // The Copilot's conversation carries candidates' details: gone on sign-out.
    copilotContext?.reset();
    copilotContext?.setOpen(false);
    if (standalone) { standaloneSignOut(); return; }
    authService.logout();
    navigate('/login');
  };
  /* Sign out from a page (the Dashboard's own, as the frame's Home has it,
     issue 24) comes through here, so the Copilot's conversation goes too. */
  useEffect(() => {
    const out = () => handleLogout();
    window.addEventListener('hiring:sign-out', out);
    return () => window.removeEventListener('hiring:sign-out', out);
  });

  // Outside the shell: the module map from the design system. Only Hiring
  // lives in this app, so it is the one entry that navigates; modules not yet
  // live carry their Blueprint status, as in the Hiring UI kit.
  // In the shell and on the school's own app: Hiring's own pages.
  const navGroups = ownPages
    ? foldedGroups(navBlocks(hiring, admin), {
      folded: blocksFold.folded,
      activeKey: page ? page.value : null,
      rail: sidebar.collapsed,
      icon: (name, size) => <Icon name={name} size={size} />,
    })
    : MODULE_GROUPS.map((g) => ({
      label: g.label,
      items: g.items.map((it) => ({
        key: it.code.toLowerCase(),
        label: it.name,
        icon: <Icon name={it.icon} size={18} />,
        badge: it.status !== 'live'
          ? <StatusPill tone={STATUS_TONE[it.status]} dot={false}>{STATUS_LABEL[it.status]}</StatusPill>
          : null,
      })),
    }));
  const groups = sidebar.desktop
    ? [...navGroups, {
      label: '',
      items: [{
        key: COLLAPSE_ITEM,
        label: sidebar.collapsed ? 'Expand sidebar' : 'Collapse sidebar',
        icon: <Icon name="sidebar" size={18} />,
      }],
    }]
    : navGroups;
  const activeKey = ownPages ? (page ? page.value : null) : HIRING_KEY;
  const onPage = ownPages
    ? (key) => navigate(key)
    : (key) => { if (key === HIRING_KEY) navigate('/dashboard'); };
  const onSelect = (key) => {
    if (key === COLLAPSE_ITEM) return sidebar.toggle();
    if (String(key).startsWith(FOLD_ITEM)) return blocksFold.toggle(String(key).slice(FOLD_ITEM.length));
    return onPage(key);
  };

  // The school, from the signed-in session. The DPS crest isn't supplied as a
  // tenant asset yet, so the Sidebar chip and the watermark use initials.
  const school = user.companyName || '';
  const tenant = school
    ? {
      name: school,
      initials: initialsOf(school) || 'S',
      subtitle: standalone && profiles.length > 1
        ? <SchoolCardSwitcher profiles={profiles} here={pathname} />
        : (profiles.length > 1 ? 'School · switch in the top bar' : 'School'),
    }
    : null;
  const person = {
    name: user.name || user.email || 'User',
    role: user.role ? user.role.charAt(0).toUpperCase() + user.role.slice(1) : 'Member',
    initials: initialsOf(user.name || user.email || 'U') || 'U',
  };

  const actions = (
    <>
      {copilot && <CopilotButton />}
      {/* Alerts and hints, out of the Dashboard and into the top bar (29 Oct 2026). */}
      <NoticeBell />
      {!inShell && !standalone && <SchoolSwitcher profiles={profiles} />}
      {/* "Create new JD" is on the Jobs and Dashboard pages, as the other apps put a
          screen's actions in its own header (29 Oct 2026). */}
      {/* The person's menu: Sign-in and security, then Sign out (frame 1.6.0). */}
      {!inShell && <PersonMenu person={person} schoolAddress={SCHOOL_ADDRESS} onSignOut={handleLogout} />}
    </>
  );
  const mobileActions = (
    <>
      {copilot && <CopilotButton />}
      <NoticeBell />
      {!inShell && <PersonMenu person={person} schoolAddress={SCHOOL_ADDRESS} onSignOut={handleLogout} />}
    </>
  );

  // Below a section's first page, the phone top bar shows a back arrow.
  const onBack = isSectionRoot ? undefined : () => (page ? navigate(page.value) : navigate(-1));

  if (inFrame) return <InFrame ds={ds}>{children}</InFrame>;

  // In the ERP, with Hiring's pages in the ERP's sidebar: one navigation.
  if (inShell && shellPagesTaken()) {
    return <FramedInErp ds={ds} actions={actions}>{children}</FramedInErp>;
  }

  return (
    <div className="eq-ds eq-root">
      <AppShell
        tenant={inShell ? null : tenant}
        user={inShell ? null : person}
        groups={groups}
        activeKey={activeKey}
        onSelect={onSelect}
        breakpoint={sidebar.collapsed ? 'tablet' : undefined}
        // The top bar names the app, as the other EquaOS apps do; each page has its own title.
        crumbs={['Hiring']}
        onBack={onBack}
        actions={actions}
        mobileActions={mobileActions}
        maxWidth={ds ? 1240 : 'none'}
        assistant={choice === 'shared' ? <SharedAssistant /> : undefined}
      >
        {(layout) => {
          const top = layout.mobile ? 16 : 28;
          const bottom = layout.mobile ? 24 : 72;
          const tabs = !ownPages && (
            <nav aria-label="Hiring" className="eq-scroll-x">
              <div>
                <Tabs items={pages} value={page ? page.value : null} onChange={(to) => navigate(to)} />
              </div>
            </nav>
          );
          return (
            <ShellLayoutContext.Provider value={layout}>
              {tabs}
              {ds ? children : (
                <div className="dps-legacy" style={{ margin: `${tabs ? 0 : -top}px -${layout.pad}px -${bottom}px`, minWidth: 0 }}>
                  {children}
                </div>
              )}
              {/* AppShell draws the school watermark from `tenant`; inside the
                  shell, where the tenant chip is hidden, draw it here. */}
              {inShell && tenant && (
                <div style={{ display: 'flex', justifyContent: 'center', paddingTop: ds ? 8 : 24 }}>
                  <TenantWatermark tenant={tenant} position="inline" size={28} opacity={0.32} />
                </div>
              )}
            </ShellLayoutContext.Provider>
          );
        }}
      </AppShell>
      {copilot && <CopilotPanel />}
    </div>
  );
};

export default MainLayout;
